import { BUNDLE_CSP } from '@src/pages/Plans/data'
import { RootState } from '@src/state/types'
import { LOCAL_OR_PLAYGROUND } from '@src/utils/localOrPlayground'
import {
  demoWorkspaceMatch,
  freeTrialTCMatch,
  plansMatch,
  rootMatch,
  wildcardMatch,
  workspaceMatch,
} from '@src/utils/routes'
import { matchPath, RouteProps } from 'react-router'
import { createSelector } from 'reselect'
import { getUpsellVariation } from '../featureFlags/selectors'
import { UpsellVariation } from '../featureFlags/types'
import {
  CM_PRICE_TEST,
  CW_PRICE_TEST,
  EB_PRICE_TEST,
  SEM_CW_PRICE_TEST,
  SEM_EB_PRICE_TEST,
} from '../optimizely/constants'
import { getPathname } from '../router/selectors'
import { titleTemplate } from './constants'
import siteConfig, { cWPlaygroundSAVersion, defaultSAVersion, tld } from './siteConfig'
import { CHEGG, CITATION_MACHINE, EASYBIB, SiteConfig, SiteState } from './types'

// Set these environment variables to override the upload URL
// and Express URL exposed to the WTAI Upload widget
// A good use case for this is if you are using it on your personal
// playgrounds. If not set, the Upload Widget will use it's own configured
// values.
const WTAI_UPLOAD_URL = process.env.REACT_APP_WTAI_UPLOAD_URL
const WTAI_EXPRESS_URL = process.env.REACT_APP_WTAI_EXPRESS_URL

export const getSite = (state: RootState) => state.site

export const getSiteConfig = createSelector(
  [getSite],
  (site): SiteConfig => siteConfig(site, LOCAL_OR_PLAYGROUND)
)

export const getSiteTermsLink = createSelector(
  getSiteConfig,
  ({ termsLink }) => termsLink
)

export const getUpgradeModalTitleExp = createSelector(
  getSiteConfig,
  ({ upgradeModalTitleExp }) => upgradeModalTitleExp
)

export const isChegg = createSelector([getSite], site => site === 'chegg')

export const selectAltLogoText = createSelector(
  [getSite],
  site => `${site} – a Chegg service`
)

export const selectCopyrightYear = createSelector([getSite], site => {
  const config = siteConfig(site, LOCAL_OR_PLAYGROUND)
  return config.copyrightStartYear
})

export const selectCopyright = createSelector(
  [selectCopyrightYear],
  copyrightYear => `Copyright © ${copyrightYear}-${new Date().getFullYear()}`
)

export const selectCapitalizedSiteName = createSelector([getSite], site => {
  const config = siteConfig(site, LOCAL_OR_PLAYGROUND)
  return config.capitalizedName
})

export const selectBannerSiteName = createSelector(
  [selectCapitalizedSiteName, getUpsellVariation],
  (siteName, upsellVariation) => {
    if (upsellVariation === UpsellVariation.var1) {
      return BUNDLE_CSP
    }
    return siteName
  }
)

export const selectPlusSiteName = createSelector(
  [selectCapitalizedSiteName, isChegg, getUpsellVariation],
  (capitalizedName, isChegg, upsellVariation) => {
    if (isChegg) {
      return upsellVariation === UpsellVariation.var1
        ? 'Chegg® Study Pack'
        : `Chegg® Writing`
    }
    return `${capitalizedName}® Plus`
  }
)

export const selectLogoUrl = createSelector(
  [getSite],
  site => `http://www.${site}.${tld[site]}`
)

export const getHelpUrl = (site: SiteState) => {
  const config = siteConfig(site, LOCAL_OR_PLAYGROUND)
  return config.helpUrl
}

export const getWbeUrl = (site: SiteState) => {
  const config = siteConfig(site, LOCAL_OR_PLAYGROUND)
  return process.env.REACT_APP_WBE_API_URL || config.wbeUrl
}

export const getWtaiUrl = (site: SiteState) => {
  const config = siteConfig(site, LOCAL_OR_PLAYGROUND)
  return process.env.REACT_APP_WTAI_API_URL || config.wtaiUrl
}

export const getCfeUrl = (site: SiteState) => {
  const config = siteConfig(site, LOCAL_OR_PLAYGROUND)
  return process.env.REACT_APP_CFE_API_URL || config.cfeUrl
}

export const getCheggAuthRedirectUrl = (site: SiteState) => {
  const { cheggAuthRedirect } = siteConfig(site, LOCAL_OR_PLAYGROUND)
  return cheggAuthRedirect
}

export const getCheggAuthVersion = (site: SiteState) =>
  site === CHEGG && LOCAL_OR_PLAYGROUND ? cWPlaygroundSAVersion : defaultSAVersion

export const getSAProfile = (site: SiteState) => {
  return siteConfig(site, LOCAL_OR_PLAYGROUND).SAProfile
}

export const getCookieDomain = (site: SiteState): string => {
  const { cookieDomain } = siteConfig(site, LOCAL_OR_PLAYGROUND)
  return cookieDomain
}

export const getUploadUrl = () => WTAI_UPLOAD_URL
export const getWtaiExpressUrl = () => WTAI_EXPRESS_URL

export const getPricingTest = (site: SiteState) => {
  switch (site) {
    case CHEGG:
      return CW_PRICE_TEST
    case EASYBIB:
      return EB_PRICE_TEST
    case CITATION_MACHINE:
      return CM_PRICE_TEST
    default:
      return ''
  }
}

export const getSemPricingTest = (site: SiteState) =>
  site === CHEGG ? SEM_CW_PRICE_TEST : SEM_EB_PRICE_TEST

interface RoutePrefix {
  match: string | RouteProps
  prefix: string
}

const routePrefixes: RoutePrefix[] = [
  {
    match: freeTrialTCMatch,
    prefix: titleTemplate.pages.termsAndConditions,
  },
  {
    match: workspaceMatch,
    prefix: titleTemplate.pages.editor,
  },
  {
    match: demoWorkspaceMatch,
    prefix: titleTemplate.pages.demo,
  },
  {
    match: { path: rootMatch, exact: true },
    prefix: titleTemplate.pages.root,
  },
  {
    match: plansMatch,
    prefix: titleTemplate.pages.plans,
  },
  {
    match: wildcardMatch,
    prefix: titleTemplate.pages.notFound,
  },
]

const getRoutePrefix = createSelector([getPathname], path => {
  const matched = routePrefixes.find(({ match }) => matchPath(path, match))
  if (!matched) {
    return titleTemplate.pages.notFound
  }
  return matched.prefix
})

export const getTitle = createSelector(
  [getRoutePrefix, selectCapitalizedSiteName],
  (routePrefix, siteName) => {
    const { base, separator } = titleTemplate

    const title = [routePrefix, siteName, base].join(separator)
    return title
  }
)



// WEBPACK FOOTER //
// ./src/state/site/selectors.ts